import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ChatConversation, ChatMessage, seedChatConversations } from '../mocks/chatConversations';

export type { ChatConversation, ChatMessage };

const STORAGE_KEY = 'sipdash_chat_data';
const SESSION_KEY = 'sipdash_chat_session';
const NAME_KEY = 'sipdash_chat_name';

function generateId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function loadConversations(): ChatConversation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as ChatConversation[];
  } catch {
    // ignore
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(seedChatConversations));
  return seedChatConversations;
}

function saveConversations(convs: ChatConversation[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(convs));
}

function getOrCreateSessionId(): string {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = 'session-' + generateId();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

interface ChatContextType {
  // User side
  userSessionId: string;
  userName: string;
  setUserName: (name: string) => void;
  userMessages: ChatMessage[];
  sendUserMessage: (text: string) => void;
  userUnreadCount: number;
  // Admin side
  allConversations: ChatConversation[];
  sendAdminReply: (sessionId: string, text: string) => void;
  markConversationRead: (sessionId: string) => void;
  totalAdminUnread: number;
  refreshConversations: () => void;
}

const ChatContext = createContext<ChatContextType | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  const [userSessionId] = useState(() => getOrCreateSessionId());
  const [userName, setUserNameState] = useState<string>(
    () => localStorage.getItem(NAME_KEY) ?? 'Guest'
  );
  const [conversations, setConversations] = useState<ChatConversation[]>(() => loadConversations());

  const refresh = useCallback(() => {
    setConversations(loadConversations());
  }, []);

  // Poll every 2s for cross-tab updates
  useEffect(() => {
    const interval = setInterval(refresh, 2000);
    return () => clearInterval(interval);
  }, [refresh]);

  // Cross-tab storage event
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh();
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [refresh]);

  const setUserName = (name: string) => {
    setUserNameState(name);
    localStorage.setItem(NAME_KEY, name);
  };

  const userConversation = conversations.find((c) => c.id === userSessionId) ?? null;
  const userMessages = userConversation?.messages ?? [];

  const userUnreadCount = userMessages.filter(
    (m) => m.sender === 'admin' && !m.read
  ).length;

  const sendUserMessage = (text: string) => {
    const now = new Date().toISOString();
    const newMsg: ChatMessage = {
      id: generateId(),
      sender: 'user',
      text,
      timestamp: now,
      read: false,
    };

    const convs = loadConversations();
    const existing = convs.find((c) => c.id === userSessionId);

    let updated: ChatConversation[];
    if (existing) {
      updated = convs.map((c) =>
        c.id === userSessionId
          ? { ...c, messages: [...c.messages, newMsg], lastActivity: now }
          : c
      );
    } else {
      const newConv: ChatConversation = {
        id: userSessionId,
        userName: localStorage.getItem(NAME_KEY) ?? 'Guest',
        messages: [newMsg],
        lastActivity: now,
      };
      updated = [newConv, ...convs];
    }

    saveConversations(updated);
    setConversations(updated);
  };

  const sendAdminReply = (sessionId: string, text: string) => {
    const now = new Date().toISOString();
    const newMsg: ChatMessage = {
      id: generateId(),
      sender: 'admin',
      text,
      timestamp: now,
      read: false,
    };

    const convs = loadConversations();
    const updated = convs.map((c) =>
      c.id === sessionId
        ? { ...c, messages: [...c.messages, newMsg], lastActivity: now }
        : c
    );
    saveConversations(updated);
    setConversations(updated);
  };

  const markConversationRead = (sessionId: string) => {
    const convs = loadConversations();
    const updated = convs.map((c) =>
      c.id === sessionId
        ? {
            ...c,
            messages: c.messages.map((m) =>
              m.sender === 'user' ? { ...m, read: true } : m
            ),
          }
        : c
    );
    saveConversations(updated);
    setConversations(updated);
  };

  const totalAdminUnread = conversations.reduce(
    (sum, c) => sum + c.messages.filter((m) => m.sender === 'user' && !m.read).length,
    0
  );

  // Mark admin messages as read for the user when they open their conversation
  const markUserRead = useCallback(() => {
    const convs = loadConversations();
    const updated = convs.map((c) =>
      c.id === userSessionId
        ? {
            ...c,
            messages: c.messages.map((m) =>
              m.sender === 'admin' ? { ...m, read: true } : m
            ),
          }
        : c
    );
    saveConversations(updated);
    setConversations(updated);
  }, [userSessionId]);

  return (
    <ChatContext.Provider
      value={{
        userSessionId,
        userName,
        setUserName,
        userMessages,
        sendUserMessage,
        userUnreadCount,
        allConversations: conversations,
        sendAdminReply,
        markConversationRead,
        totalAdminUnread,
        refreshConversations: refresh,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside ChatProvider');
  return ctx;
}
